import { Form } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CreatableSelect from 'react-select/creatable';

import type { SelectOption } from './SelectField';

export interface UserComboFieldProps {
    name: string;
    label?: string;
    options: SelectOption[];
    placeholder?: string;
    required?: boolean;
}

/**
 * Searchable user picker bound to react-hook-form that ALSO allows typing a brand-new value
 * (react-select Creatable). Used for the SmartStore/Jasper "Brukernavn" field: pick an existing
 * SSO user from the list, or type the e-mail of an AD user not yet imported — the backend
 * (EvaluateApplication -> FindAdUser -> ADUserToDatabase) auto-creates them on submit. The bound
 * value stays a plain string (username/e-mail), so the existing zod e-mail validation still applies.
 */
export function UserComboField({ name, label, options, placeholder, required }: UserComboFieldProps) {
    const {
        control,
        formState: { errors },
    } = useFormContext();
    const { t } = useTranslation();
    const error = errors[name];
    const message = typeof error?.message === 'string' ? t(error.message) : undefined;
    const errorId = `${name}-error`;

    return (
        <Form.Group className="mb-3" controlId={name}>
            {label && (
                <Form.Label htmlFor={name}>
                    {label}
                    {required && (
                        <span className="field-required-mark" aria-hidden="true">
                            *
                        </span>
                    )}
                </Form.Label>
            )}
            <Controller
                control={control}
                name={name}
                render={({ field }) => (
                    <CreatableSelect<SelectOption, false>
                        inputId={name}
                        options={options}
                        isClearable
                        placeholder={placeholder ?? t('common.selectPlaceholder')}
                        noOptionsMessage={() => t('common.noOptions')}
                        formatCreateLabel={(input) => t('common.createOption', { value: input })}
                        classNamePrefix="dkn-rs"
                        className={error ? 'is-invalid' : undefined}
                        aria-invalid={!!error}
                        aria-errormessage={error ? errorId : undefined}
                        value={
                            options.find((o) => o.value === field.value) ??
                            (field.value ? { value: field.value as string, label: field.value as string } : null)
                        }
                        onBlur={field.onBlur}
                        onChange={(opt) => field.onChange(opt?.value ?? '')}
                    />
                )}
            />
            {message && (
                <div className="invalid-feedback d-block" id={errorId}>
                    {message}
                </div>
            )}
        </Form.Group>
    );
}
